/* @flow */

import * as React from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import Router from 'next/router';

import { Link } from '../../routes';
import FlexBox from '../FlexBox';
import Icon from '../Icon';
import withi18n from '../../utils/withI18n';
import { getLabel } from '../../utils/i18n';
import { formatNumber } from '../../utils/number';
import UserAvatar from '../UserCenter/UserBaseInfo/UserAvatar';
import AttentionSwitch from '../AttentionSwitch';

const Wrap = styled(FlexBox)`
  min-width: 320px;
  background: #1E2126;
`;

const BaseInfoBox = styled(FlexBox)`
  width: 100%;
  .email {
    color: #fff;
    font-size: 20px;
    font-weight: normal;
    margin: 0 49px 3px 0;
    + div {
      width: 100%;
    }
  }
  .bnc-avatar {
    color: #fff;
  }
  .kycBox a {
    margin-top: 2px;
    height: 18px;
    line-height: 16px;
    padding: 0 6px;
  }
  .linkTo:hover .iconfont{
    color: ${({ theme }) => theme.highlight}!important;
  }
  .kycBox:hover a {
    text-decoration: underline;
  }
`;

const EstimatedValue = styled.div`
  padding: 16px 20px 10px;
  > .num {
    font-size: 24px;
    font-family: ${({ theme }) => theme.fontIBMBold};
    font-weight: 500;

    line-height: 32px;
    margin-top: 6px;
    > a, > a:visited {
      color: #fff;
    }

    > a:hover {
      color: #f0b90b;
    }
    > span {
      margin-left: 4px;
    }
  }
  .icon-attention:hover,
  .icon-attention-forbid:hover {
    color: ${({ theme }) => theme.highlight};
  }
`;

const StyledLabel = styled(FlexBox)`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
`;

const StyledButton = styled.button`
  display: inline-block;
  width: 100%;
  height:38px;
  line-height: 36px;
  border-radius:4px;
  background: none;
  border:1px solid rgba(240,185,10,1);
  cursor: pointer;
  color: #F0B90A;
  img {
    margin-right: 10px;
    :last-of-type {
      display: none;
    }
  }
  :hover {
    background:linear-gradient(90deg,rgba(240,185,11,1) 0%,rgba(251,218,60,1) 100%);
    color: #212833;
    img {
      :first-of-type {
        display: none;
      }
      :last-of-type {
        display: inline-block;
      }
    }
  }
`;

const LinksList = styled(FlexBox)`
  width: 100%;
  .iconfont {
    margin-right: 10px;
  }
  a,
  a:visited {
    display: block;
    line-height: 100%;
    padding: 7px 5px 7px 20px;
    color: #fff;
    font-size: ${({ theme }) => theme.fontSizeMd};
    > img.new {
      margin-left: 5px;
    }
  }
  .icons {
    img {
      margin-right: 10px;
      vertical-align: center;
    }
    img:last-of-type {
      display: none;
    }
  }
  a:hover, a.active {
    color: ${({ theme }) => theme.highlight};
    background: rgba(255, 255, 255, 0.08);
    .icons img:first-of-type {
     display: none;
    }
    .icons img:last-of-type {
      display: inline-block;
    }
  }
  a:hover .iconfont {
    color: ${({ theme }) => theme.highlight};
  }

`;

const SideList = styled(FlexBox)`
  a {
    padding: 10px 5px 10px 20px;
  }
`;

const UserBaseInfoBox = styled(FlexBox)`
  width: auto;
`;

const UserBaseInfoWrap = styled(FlexBox)`
  width: 100%;
  padding: 0 20px;
  :hover {
    background:rgba(255,255,255,0.04);
  }
`;

const UserBaseInfoInner = styled(FlexBox)`
  width: 100%;
  padding: 20px 0;
  border-bottom: 1px solid rgba(242,242,242,0.1);
`;

const EmailBox = styled.div`
    font-size: 16px;
    color: #fff;
    margin-right: 10px;
    line-height: 100%;
`;

const VipBox = styled(FlexBox)`
  font-size: ${({ theme }) => theme.fontSizeMd};
  color: ${({ theme }) => theme.highlight};
  line-height: 24px;
  .iconfont {
    margin: -2px 6px 0 0;
    line-height: 28px;
  }
`;

const Tag = styled.span`
  display: inline-block;
  vertical-align: middle;
  height: 18px;
  line-height: 17px;
  padding: 0 6px;
  border-radius: 2px;
  background-color: ${({ type }) => type === 'unVerified' ? 'rgba(132,142,156,0.3)' : 'rgba(240,185,11,0.3)'};
  font-size: ${({ theme }) => theme.fontSizeSm};
  color: ${({ type }) => type === 'unVerified' ? '#848E9C' : '#F0B90B'};
  margin-left: 6px;
`;

const LinkButton = styled.div`
  width: 100%;
  padding: 0 20px;
  margin: 10px 0 20px;
`;

type UserAssetsBTCValues = {
  totalTransferBtc: number,
};

type Props = {
  locale: string,
  i18n: Object,
  userInfo: Object,
  allUserAssetsBTCValues: UserAssetsBTCValues,
  logout: Function,
  shouldPublicAssets: boolean,
};

type State = {
  isShow: boolean,
};

const mapStateToProps = ({
  locale, userInfo, allUserAssetsBTCValues, shouldPublicAssets,
}) => ({
  locale,
  userInfo,
  allUserAssetsBTCValues,
  shouldPublicAssets,
});

export default
@withi18n
@connect(mapStateToProps)
class UserInfoPanel extends React.PureComponent<Props, State> {
  static defaultProps = {
    locale: 'en',
    i18n: {},
    userInfo: {},
    allUserAssetsBTCValues: { totalTransferBtc: 0 },
    logout: () => { },
    shouldPublicAssets: true,
  };

  _handleLogout = (ev: MouseEvent) => {
    this.props.logout(ev, this.props.locale);
  };

  _renderKYC = () => {
    const {
      userInfo: {
        idPhoto,
      },
      i18n,
    } = this.props;
    if (idPhoto === '1') {
      return <Tag>{getLabel('exchange-ProfileIndex-Verified', i18n)}</Tag>;
    }
    return <Tag type="unVerified">{getLabel('exchange-ProfileIndex-NotVerified', i18n)}</Tag>;
  }

  _goToDashboard = () => {
    Router.push(`/${this.props.locale}/usercenter/dashboard/overview`);
  }

  render() {
    const {
      locale,
      i18n,
      allUserAssetsBTCValues: { totalTransferBtc },
      userInfo,
      shouldPublicAssets,
    } = this.props;
    const {
      email, tradeLevel,
    } = userInfo;
    // $FlowFixMe
    const emailPrefix = email ?.substr(0, 2);
    // $FlowFixMe
    const emailSuffix = email ?.split('@')[1];
    return (
      <Wrap>
        <BaseInfoBox direction="column">
          <UserBaseInfoWrap align="center" wrap="nowrap" onClick={this._goToDashboard}>
            <UserBaseInfoInner justify="space-between" align="center" wrap="nowrap">
              <UserBaseInfoBox align="flex-start" wrap="nowrap">
                <UserAvatar text={emailPrefix} />
                <FlexBox margin="0 0 0 10px" direction="column">
                  <FlexBox margin="0 0 4px 0">
                    <EmailBox>
                      {`${emailPrefix}***@${emailSuffix}`}
                    </EmailBox>
                  </FlexBox>
                  <FlexBox wrap="nowrap" align="center">
                    <VipBox wrap="nowrap">
                      <Icon type="vip" size={16} color="#f0b90b" />
                      <span>{` VIP ${tradeLevel}`}</span>
                    </VipBox>
                    {this._renderKYC()}
                  </FlexBox>
                </FlexBox>
              </UserBaseInfoBox>
              <Icon type="dropdown-right" size={16} color="#fff" />
            </UserBaseInfoInner>
          </UserBaseInfoWrap>
          <EstimatedValue>
            <StyledLabel align="center">
              {getLabel('exchange-header-estimatedValue', i18n)}
              <AttentionSwitch status={shouldPublicAssets} shouldBorder={false} buttonId="UserInfoPanel-button-estimatedValue" />
            </StyledLabel>
            <div className="num">
              <Link route={`/${locale}/usercenter/wallet/balances`}>
                <a id="exchange-header-balance">
                  {shouldPublicAssets ? `${formatNumber(totalTransferBtc, 8)}` : '******'}
                  {shouldPublicAssets && <span>BTC</span>}
                </a>
              </Link>
            </div>
          </EstimatedValue>
          <LinkButton basis="100%">
            <Link route={`/${locale}/activity/referral`}>
              <a id="exchange-header-referral">
                <StyledButton>
                  <img src={require('./Referral_yellow.svg')} alt="withdrawal" width="20" />
                  <img src={require('./Referral.svg')} alt="withdrawal" width="20" />
                  {getLabel('exchange-header-referFriendsToEarn', i18n)}
                </StyledButton>
              </a>
            </Link>
          </LinkButton>

        </BaseInfoBox>
        <LinksList direction="column">
          <FlexBox>
            <FlexBox flex="1" direction="column">
              <StyledLabel margin="0 0 7px 20px">{getLabel('exchange-nav-wallet', i18n)}</StyledLabel>
              <Link route={`/${locale}/usercenter/wallet/balances`}>
                <a id="exchange-header-balance">
                  <span className="icons">
                    <img src={require('./Balance.svg')} alt="balance" width="20" />
                    <img src={require('./Balance_yellow.svg')} alt="balance" width="20" />
                  </span>
                  {getLabel('exchange-nav-balances', i18n)}
                </a>
              </Link>
              {!userInfo.isSubUser && (
                <Link route={`/${locale}/usercenter/wallet/deposit/BTC`}>
                  <a id="exchange-header-deposit">
                    <span className="icons">
                      <img src={require('./Deposit.svg')} alt="deposit" width="20" />
                      <img src={require('./Deposit_yellow.svg')} alt="deposit" width="20" />
                    </span>
                    {getLabel('exchange-nav-deposit', i18n)}
                  </a>
                </Link>)}
              {!userInfo.isSubUser && (
                <Link route={`/${locale}/usercenter/wallet/withdrawals/BTC`}>
                  <a id="exchange-header-withdraw">
                    <span className="icons">
                      <img src={require('./Withdrawal.svg')} alt="withdrawal" width="20" />
                      <img src={require('./Withdrawal_yellow.svg')} alt="withdrawal" width="20" />
                    </span>
                    {getLabel('exchange-nav-withdraw', i18n)}
                  </a>
                </Link>)}
            </FlexBox>
            <FlexBox flex="1" direction="column">
              <StyledLabel margin="0 0 7px 20px">{getLabel('exchange-nav-orders', i18n)}</StyledLabel>
              <Link route={`/${locale}/usercenter/history/open-order`}>
                <a id="exchange-header-openOrder">
                  <span className="icons">
                    <img src={require('./OpenOrders.svg')} alt="withdrawal" width="20" />
                    <img src={require('./OpenOrders_yellow.svg')} alt="withdrawal" width="20" />
                  </span>
                  {getLabel('exchange-nav-openOrder', i18n)}
                </a>
              </Link>
              <Link route={`/${locale}/usercenter/history/trade-order`}>
                <a id="exchange-header-tradeHistory">
                  <span className="icons">
                    <img src={require('./OrderTradeHistory.svg')} alt="withdrawal" width="20" />
                    <img src={require('./OrderTradeHistory_yellow.svg')} alt="withdrawal" width="20" />
                  </span>
                  {getLabel('exchange-nav-orderHistory', i18n)}
                </a>
              </Link>
              <Link route={`/${locale}/usercenter/history/user-trade`}>
                <a id="exchange-header-userTradeHistory">
                  <span className="icons">
                    <img src={require('./OrderTradeHistory.svg')} alt="withdrawal" width="20" />
                    <img src={require('./OrderTradeHistory_yellow.svg')} alt="withdrawal" width="20" />
                  </span>
                  {getLabel('exchange-nav-tradeHistory', i18n)}
                </a>
              </Link>
            </FlexBox>
          </FlexBox>
          <SideList direction="column" margin="18px 0 0">
            <Link route={`/${locale}/buy-sell-crypto`}>
              <a id="exchange-header-creditCard">
                <span className="icons">
                  <img src={require('./BuyWithCreditCard.svg')} alt="withdrawal" width="20" />
                  <img src={require('./BuyWithCreditCard_yellow.svg')} alt="withdrawal" width="20" />
                </span>

                {getLabel('exchange-header-creditCard', i18n)}
                <img className="new" src={require('./new.svg')} alt="withdrawal" />
              </a>
            </Link>
            <Link route={`/${locale}/usercenter/security`}>
              <a id="exchange-header-accountActivity">
                <span className="icons">
                  <img src={require('./AccountSecurity.svg')} alt="withdrawal" width="20" />
                  <img src={require('./AccountSecurity_yellow.svg')} alt="withdrawal" width="20" />
                </span>
                {getLabel('exchange-nav-security', i18n)}
              </a>
            </Link>

            <Link route={`/${locale}/usercenter/settings/api-management`}>
              <a id="exchange-header-creditCard">
                <Icon type="APIManagement-x-darkbg" size={20} color="#808080" />
                {getLabel('exchange-nav-APIManagement', i18n)}
              </a>
            </Link>

            <a id="exchange-header-logout" onClick={this._handleLogout}>
              <span className="icons">
                <img src={require('./LogOut.svg')} alt="withdrawal" width="20" />
                <img src={require('./LogOut_yellow.svg')} alt="withdrawal" width="20" />
              </span>
              {getLabel('exchange-nav-logOut', i18n)}
            </a>
          </SideList>
        </LinksList>

      </Wrap>
    );
  }
}
